import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import MarketBenchmark from "@/components/MarketBenchmark";
import AILearning from "@/components/AILearning";
import ManualEvents from "@/components/ManualEvents";
import Collaborators from "@/components/Collaborators";
import PricingIntelligence from "@/components/PricingIntelligence";
import SelfCalibration from "@/components/SelfCalibration";
import PremiumFloorSettings from "@/components/PremiumFloorSettings";
import FloorHistoryTable from "@/components/FloorHistoryTable";
import AppliedHistory from "@/components/AppliedHistory";
import GoogleVisibility from "@/components/GoogleVisibility";
import MealPlanReview from "@/components/MealPlanReview";
import RatePerformance from "@/components/RatePerformance";
import PerformanceTrend from "@/components/PerformanceTrend";
import BookingPace from "@/components/BookingPace";
import LastMinuteStats from "@/components/LastMinuteStats";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Building2, Loader2, Sparkles, TrendingUp, TrendingDown, CheckCircle2, AlertTriangle,
  ListChecks, ArrowRight, Camera, ThumbsUp, ThumbsDown, Clock,
} from "lucide-react";

const DOT = { high: "#ef4444", medium: "#f59e0b", low: "#22c55e" };

function OwnPhotoInsightCard({ propId, lang, L }) {
  const [meta, setMeta] = useState(null);
  const [hasBooking, setHasBooking] = useState(true);
  const [loading, setLoading] = useState(false);
  const ins = meta?.insight || null;
  const canReanalyze = meta ? !!meta.can_reanalyze : true;
  const fmtD = (iso) => { try { const d = new Date(iso); return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; } catch { return "—"; } };

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const { data } = await api.get(`/properties/${propId}/own-photo-insight`);
        if (!on) return;
        setMeta(data.meta || null);
        setHasBooking(!!data.has_booking_url);
      } catch { /* noop */ }
    })();
    return () => { on = false; };
  }, [propId]);

  const run = async () => {
    if (ins && !canReanalyze) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/properties/${propId}/own-photo-insight`, { lang });
      setMeta({ insight: data.photo_insight, analyzed_at: data.analyzed_at, age_days: data.age_days,
                can_reanalyze: data.can_reanalyze, next_available_at: data.next_available_at,
                image_urls: data.image_urls || [] });
      toast.success(data.cached ? L("Analisi recuperata", "Analysis loaded") : L("Foto analizzate", "Photos analyzed"));
    } catch (e) { toast.error(e?.response?.data?.detail || L("Analisi non riuscita", "Analysis failed")); }
    finally { setLoading(false); }
  };
  const col = (v) => (v >= 66 ? "#16a34a" : v >= 40 ? "#b45309" : "#ef4444");
  const btnDisabled = loading || (!!ins && !canReanalyze);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6" data-testid="mp-photo-insight">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2"><Camera className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">{L("Analisi foto (occhio del cliente)", "Photo analysis (guest's eye)")}</h2></div>
        <div className="flex items-center gap-2">
          {ins && !canReanalyze && meta?.next_available_at && (
            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1" data-testid="mp-photo-next">
              <Clock className="w-3.5 h-3.5" />{L(`Nuova analisi dal ${fmtD(meta.next_available_at)}`, `New analysis from ${fmtD(meta.next_available_at)}`)}
            </span>
          )}
          <Button data-testid="mp-photo-analyze-btn" size="sm" variant={ins ? "outline" : "default"} onClick={run} disabled={btnDisabled}
            title={ins && !canReanalyze ? L("Una nuova analisi è consentita una volta al mese", "A new analysis is allowed once a month") : undefined}>
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            {ins ? L("Rianalizza", "Re-analyze") : L("Analizza foto", "Analyze photos")}
          </Button>
        </div>
      </div>
      {!hasBooking && !ins ? (
        <p className="text-sm text-amber-600 dark:text-amber-400 flex items-start gap-2" data-testid="mp-photo-no-booking">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{L("Aggiungi il link Booking della tua struttura (Modifica struttura) per attivare l'analisi delle foto.", "Add your Booking link (Edit property) to enable photo analysis.")}
        </p>
      ) : !ins ? (
        <p className="text-sm text-muted-foreground">{L("Analizza le foto del tuo annuncio Booking: l'AI le valuta come un cliente che deve prenotare e ti dice cosa trasmettono, punti di forza, punti deboli e un punteggio 0-100. Una nuova analisi è consentita una volta al mese.", "Analyze your Booking listing photos: the AI reviews them as a guest about to book and tells you what they convey, strengths, weaknesses and a 0-100 score. A new analysis is allowed once a month.")}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="text-center shrink-0">
              <div className="font-data text-3xl font-bold" style={{ color: col(ins.score) }} data-testid="mp-photo-score">{ins.score}<span className="text-sm text-muted-foreground font-normal">/100</span></div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Photo Score</div>
            </div>
            <div>
              {ins.headline && <p className="font-semibold text-sm">{ins.headline}</p>}
              {ins.perception && <p className="text-sm text-muted-foreground mt-0.5">{ins.perception}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ins.pros?.length > 0 && <div><p className="text-xs uppercase font-bold text-emerald-600 mb-1.5 inline-flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" />{L("Punti di forza", "Pros")}</p><ul className="space-y-1">{ins.pros.map((p, i) => <li key={i} className="text-sm flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />{p}</li>)}</ul></div>}
            {ins.cons?.length > 0 && <div><p className="text-xs uppercase font-bold text-amber-600 mb-1.5 inline-flex items-center gap-1"><ThumbsDown className="w-3.5 h-3.5" />{L("Punti deboli", "Cons")}</p><ul className="space-y-1">{ins.cons.map((p, i) => <li key={i} className="text-sm flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />{p}</li>)}</ul></div>}
          </div>
          {ins.verdict && <p className="text-sm border-t border-border pt-3"><b>{L("Verdetto", "Verdict")}:</b> {ins.verdict}</p>}
          {(meta?.image_urls?.length > 0) && (
            <div className="border-t border-border pt-3" data-testid="mp-photo-thumbs">
              <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{L("Foto analizzate dall'AI", "Photos analyzed by the AI")}</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {meta.image_urls.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer" data-testid={`mp-photo-thumb-${i}`}
                    className="block aspect-square rounded-md overflow-hidden border border-border bg-muted hover:opacity-80 transition-opacity">
                    <img src={u} alt={`foto ${i + 1}`} loading="lazy" className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                  </a>
                ))}
              </div>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">{L(`Foto analizzate: ${ins.photos_analyzed}${meta?.analyzed_at ? ` · analisi del ${fmtD(meta.analyzed_at)}` : ""} · consulenza sulla tua struttura (non influisce sul prezzo).`, `Photos analyzed: ${ins.photos_analyzed}${meta?.analyzed_at ? ` · analyzed ${fmtD(meta.analyzed_at)}` : ""} · advisory for your property (does not affect pricing).`)}</p>
        </div>
      )}
    </motion.div>
  );
}

export default function MyProperty() {
  const { lang, properties, activeId, user } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const nav = useNavigate();
  const active = properties.find((p) => p.id === activeId) || properties[0];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!active?.id) return;
    setLoading(true);
    try { const { data } = await api.get(`/properties/${active.id}/advisor-summary?lang=${lang}`); setData(data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [active?.id, lang]);

  useEffect(() => { load(); }, [load]);

  const perf = data?.performance || {};
  const ai = data?.ai || {};

  const Kpi = ({ label, value, sub, testid }) => (
    <div className="bg-muted/40 rounded-lg p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</p>
      <p className="font-data text-2xl font-semibold mt-1">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div className="App min-h-screen"><Nav />
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="my-property-page">
        <div className="flex items-center gap-3 mb-6">
          <Building2 className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{L("La mia struttura", "My property")}</h1>
            <p className="text-sm text-muted-foreground">{active?.name} · {active?.city}</p>
          </div>
        </div>

        {active?.id && <div className="mb-6"><SelfCalibration propId={active.id} lang={lang} /></div>}

        {active?.id && <div className="mb-6"><PremiumFloorSettings propId={active.id} lang={lang} initialPct={active.premium_floor_trim_pct} /></div>}

        {active?.id && <div className="mb-6"><FloorHistoryTable propId={active.id} lang={lang} /></div>}

        {loading ? (
          <div className="py-24 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />{L("Preparo la consulenza…", "Preparing your consultancy…")}</div>
        ) : !data ? (
          <div className="py-24 text-center text-muted-foreground">{L("Nessun dato disponibile.", "No data available.")}</div>
        ) : (
          <div className="space-y-6">
            {/* Performance summary */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-lg p-6" data-testid="mp-performance">
              <h2 className="text-sm uppercase tracking-widest font-bold text-muted-foreground mb-4">{L("Riepilogo performance", "Performance summary")}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi testid="mp-adr" label="ADR" value={perf.adr != null ? `€${Math.round(perf.adr)}` : "—"} sub={perf.revpar != null ? `RevPAR €${Math.round(perf.revpar)}` : ""} />
                <Kpi testid="mp-occ" label={L("Occupazione", "Occupancy")} value={perf.occupancy != null ? `${Math.round(perf.occupancy)}%` : "—"} />
                <Kpi testid="mp-fwd" label="Forward Occupancy" value={perf.forward_occupancy_index != null ? `${perf.forward_occupancy_index}%` : "—"} sub={perf.forward_occupancy_prev_year != null ? L(`Anno prec. ${perf.forward_occupancy_prev_year}%`, `Prev yr ${perf.forward_occupancy_prev_year}%`) : ""} />
                <Kpi testid="mp-pickup" label={L("Pickup 7gg", "7-day pickup")} value={perf.pickup_weekly != null ? perf.pickup_weekly : "—"} sub={perf.lead_time != null ? L(`Lead time ${Math.round(perf.lead_time)}gg`, `Lead time ${Math.round(perf.lead_time)}d`) : ""} />
              </div>
            </motion.div>

            {/* AI consultancy */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="bg-card border-2 border-primary/25 rounded-lg p-6" data-testid="mp-ai">
              <div className="flex items-center gap-2 mb-3"><Sparkles className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">{L("Consulenza AI", "AI consultancy")}</h2></div>
              {ai.headline && <p className="text-base font-semibold mb-1" data-testid="mp-ai-headline">{ai.headline}</p>}
              {ai.assessment && <p className="text-sm text-muted-foreground mb-4">{ai.assessment}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(ai.strengths || []).length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wide font-bold text-emerald-600 mb-2">{L("Punti di forza", "Strengths")}</p>
                    <ul className="space-y-1.5">
                      {ai.strengths.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{s}</li>)}
                    </ul>
                  </div>
                )}
                {(ai.weaknesses || []).length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wide font-bold text-amber-600 mb-2">{L("Aree di miglioramento", "Weaknesses")}</p>
                    <ul className="space-y-1.5">
                      {ai.weaknesses.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm"><AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              {(ai.priorities || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-xs uppercase tracking-wide font-bold text-primary mb-2">{L("Priorità consigliate", "Recommended priorities")}</p>
                  <ol className="space-y-1.5" data-testid="mp-priorities">
                    {ai.priorities.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>{s}</li>)}
                  </ol>
                </div>
              )}
            </motion.div>

            {/* Photo analysis of the owner's own Booking listing (guest's eye) */}
            <OwnPhotoInsightCard propId={active.id} lang={lang} L={L} />

            {/* Google Business Profile visibility */}
            <GoogleVisibility propId={active.id} lang={lang} />

            {/* Meal plan classification & correction */}
            <MealPlanReview propId={active.id} lang={lang} />

            {/* Performance over time (own imported data) */}
            <PerformanceTrend propId={active.id} lang={lang} />

            {/* Demand pace / booking lead time */}
            <BookingPace propId={active.id} lang={lang} />

            {/* Last-minute bookings per month */}
            <LastMinuteStats propId={active.id} lang={lang} />

            {/* Rate performance analytics */}
            <RatePerformance propId={active.id} lang={lang} />

            {/* AI Learning */}
            <AILearning propId={active.id} lang={lang} />

            {/* Manual Event Manager */}
            <ManualEvents propId={active.id} lang={lang} />

            {["owner", "admin"].includes(user?.role) && (
              <Collaborators propId={active.id} lang={lang} />
            )}

            {/* Pricing Intelligence v2 */}
            <PricingIntelligence propId={active.id} lang={lang} />

            {/* Full market benchmark (reused) */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <MarketBenchmark propId={active.id} lang={lang} date={new Date().toISOString().slice(0, 10)} />
            </div>

            {/* Applied history & learning-loop outcomes */}
            <AppliedHistory propId={active.id} lang={lang} />
          </div>
        )}
      </div>
    </div>
  );
}
